import { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  UserPlus, 
  Search, 
  ChevronDown, 
  MoreVertical, 
  Edit, 
  Trash2, 
  X,
  Download,
  CheckCircle,
  XCircle,
  UserCheck,
  CreditCard,
  Calendar,
  Clock,
  DollarSign,
  Package,
  TrendingUp,
  Gift,
  Eye,
  FileText,
  Plus,
  Menu
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import imageApi from '../services/imageApi';

const CUSTOMER_API_BASE = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/customer';
const PLANS_API_BASE = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/plans';
const PLAN_INVOICE_API_BASE = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/plansInvoices';
const PRODUCTS_API_BASE = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/products';
const DEPOSITS_API_BASE = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/deposits';

function getAuthHeaders() {
  const token = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('access_token'))
    || (typeof localStorage !== 'undefined' && localStorage.getItem('access_token'));
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Customers() {
  // State management
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [memberFilter, setMemberFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAddingCustomer, setIsAddingCustomer] = useState(false); // New loading state for Add Customer
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCustomerDetail, setSelectedCustomerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('Customer Information');
  const [overlayMessage, setOverlayMessage] = useState('');
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState({ url: '', number: '' });
  const [allProducts, setAllProducts] = useState([]);
  const [isEditingDetails, setIsEditingDetails] = useState(false);

  // State for adding a plan directly from the Plan Tab
  const [showInlineAddPlan, setShowInlineAddPlan] = useState(false);
  const [inlinePlanData, setInlinePlanData] = useState({ planId: '', quantity: 1, purchase_price: '', startDate: '', endDate: '', status: 'active' });
  // State for Plan Price Inline Editing
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editPurchasePrice, setEditPurchasePrice] = useState('');

  // Deposit State
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [isEditingDeposit, setIsEditingDeposit] = useState(false);
  const [activePlanForDeposit, setActivePlanForDeposit] = useState(null);
  const [depositFormData, setDepositFormData] = useState({ depositId: '', productId: '', purchase_price: '' });

  // Manual Credit State
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditFormData, setCreditFormData] = useState({ type: 'time_credit', amount: '', unit: 'minutes', expireInDays: 30 });

  // Plan Invoice State 
  const [showPlanInvoiceModal, setShowPlanInvoiceModal] = useState(false);
  const [invoiceFormData, setInvoiceFormData] = useState({ 
    planId: '', description: '', price: '', tax: '11', totalAmount: '', dueDate: '', deposits: [],
    bank_name: '', bank_code: '', account_number: '', sort_code: ''
  });

  // pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [customersPerPage] = useState(10);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    customerType: 'individual',
    companyName: '',
    country: '',
    city: '',
    zipcode: '',
    address: '',
    isSuspended: false,
    invoiceDescription: '',
    invoicePrice: '',
    invoiceTax: '11',
    invoiceTotalAmount: '',
    invoiceDueDate: '',
    profilePicture: '',
    phoneNumbers: [{ label: 'Mobile', number: '' }],
    payment_method: 'Cash', 
    bank_name: '', 
    bank_code: '', 
    sort_code: '', 
    account_number: '',
    selectedPlans: [] 
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const menuRef = useRef(null);

  // Helper to strictly format dates and plans to match Backend Schema
  const formatPlansForApi = (plansArray) => {
    return plansArray.map(p => {
      const planDetails = plans.find(pl => pl._id === (p.planId || p._id));
      const purchasePrice = (p.purchase_price !== '' && p.purchase_price !== undefined) 
          ? p.purchase_price 
          : (planDetails ? parseFloat(typeof planDetails.price === 'string' ? planDetails.price.replace(/[^0-9.-]+/g, "") : planDetails.price) : 0);   

      let start_date = p.start_date || p.startDate;
      if (start_date && !start_date.includes('T')) {
        start_date = new Date(start_date).toISOString();
      }
      
      let end_date = p.end_date || p.endDate;
      if (end_date && !end_date.includes('T')) {
        end_date = new Date(end_date).toISOString();
      }

      return {
        planId: p.planId || p._id,
        quantity: Number(p.quantity) || 1,
        purchase_price: Number(purchasePrice) || 0,
        start_date: start_date || null,
        end_date: end_date || null,
        status: p.status || 'active'
      };
    });
  };

  // --- HANDLERS FOR MULTIPLE PLANS ---
  const handleAddSelectedPlan = () => {
    setFormData(prev => ({
      ...prev,
      selectedPlans: [...prev.selectedPlans, { planId: '', quantity: 1, purchase_price: '', startDate: '', endDate: '', status: 'active' }]
    }));
  };

  const handleRemoveSelectedPlan = (index) => {
    const updatedPlans = formData.selectedPlans.filter((_, i) => i !== index);
    recalculateInvoice(updatedPlans);
  };

  const handleSelectedPlanChange = (index, field, value) => {
    const updatedPlans = [...formData.selectedPlans];
    updatedPlans[index] = { ...updatedPlans[index], [field]: value };

    if (field === 'planId') {
      const selectedPlan = plans.find(p => p._id === value);
      if (selectedPlan) {
        const rawPrice = typeof selectedPlan.price === 'string' ? parseFloat(selectedPlan.price.replace(/[^0-9.-]+/g, "")) : selectedPlan.price;
        updatedPlans[index].purchase_price = rawPrice;
      } else {
        updatedPlans[index].purchase_price = '';
      }
    }

    setFormData(prev => ({ ...prev, selectedPlans: updatedPlans }));

    recalculateInvoice(updatedPlans);
  };

  const recalculateInvoice = (updatedPlans) => {
    setFormData(prev => {
      let totalPrice = 0;
      let descriptions = [];

      updatedPlans.forEach(p => {
        if (p.planId) {
          const plan = plans.find(pl => pl._id === p.planId);
          if (plan) {
            const rawPrice = typeof plan.price === 'string' ? parseFloat(plan.price.replace(/[^0-9.-]+/g, "")) : plan.price;
            totalPrice += (rawPrice * (p.quantity || 1));
            descriptions.push(`${plan.name} (x${p.quantity || 1})`);
          }
        }
      });

      const invoicePrice = totalPrice.toString();
      const invoiceDescription = descriptions.length > 0 ? descriptions.join(', ') + ' Subscription' : '';
      const tax = parseFloat(prev.invoiceTax) || 0;
      const invoiceTotalAmount = (totalPrice + (totalPrice * tax / 100)).toFixed(2);

      return {
        ...prev,
        selectedPlans: updatedPlans,
        invoicePrice,
        invoiceDescription,
        invoiceTotalAmount
      };
    });
  };

  const handleInvoiceFieldChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'invoicePrice' || field === 'invoiceTax') {
        const price = parseFloat(field === 'invoicePrice' ? value : updated.invoicePrice) || 0;
        const tax = parseFloat(field === 'invoiceTax' ? value : updated.invoiceTax) || 0;
        updated.invoiceTotalAmount = (price + (price * tax / 100)).toFixed(2);
      }
      return updated;
    });
  };

  const startEditingDetails = () => {
    const c = selectedCustomerDetail.customer || {};
    const addressObj = selectedCustomerDetail.address || {};
    const bankObj = selectedCustomerDetail.bank_details || {};
    
    const existingPlans = selectedCustomerDetail.plans?.length > 0 ? selectedCustomerDetail.plans : [];

    const formattedPlans = existingPlans.map(p => ({
      planId: p.planId || p._id || '',
      quantity: p.quantity || 1,
      purchase_price: p.customer_price || p.purchase_price || '',
      startDate: p.start_date ? new Date(p.start_date).toISOString().split('T')[0] : '',
      endDate: p.end_date ? new Date(p.end_date).toISOString().split('T')[0] : '',
      status: p.status || 'active'
    }));

    setFormData({
      fullName: c.name || c.fullName || '',
      email: c.email || '',
      customerType: c.customerType || 'individual',
      isSuspended: c.isSuspended || false,
      companyName: c.companyName || '',
      profilePicture: c.profilePicture || '',
      phoneNumbers: c.phoneNumbers?.length > 0 
        ? c.phoneNumbers.map(p => ({ 
            label: p.label ? p.label.charAt(0).toUpperCase() + p.label.slice(1).toLowerCase() : 'Mobile', 
            number: p.number || '' 
          })) 
        : [{ label: 'Mobile', number: '' }],
      address: addressObj.address || '',
      city: addressObj.city || '',
      zipcode: addressObj.zipcode || '',
      country: addressObj.country || '',
      payment_method: c.payment_method || 'Cash',
      bank_name: bankObj.bank_name || '',
      bank_code: bankObj.bank_code || '',
      sort_code: bankObj.sort_code || '',
      account_number: bankObj.account_number || '',
      selectedPlans: formattedPlans,
      invoiceDescription: '', invoicePrice: '', invoiceTax: '11', invoiceTotalAmount: '', invoiceDueDate: ''
    });
    setIsEditingDetails(true);
  };

  const handlePreviewInvoice = (url, number) => {
    setPreviewData({ url, number });
    setShowPreviewModal(true);
  };

  // --- IMAGE & PHONES ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setUploadingImage(true);
        const result = await imageApi.uploadImage(file);
        const responseData = result.data || result; 
        const imageUrl = responseData.url || responseData.imageUrl || responseData.image_url || responseData.secure_url;
        
        if (imageUrl) {
          setFormData(prev => ({ ...prev, profilePicture: imageUrl }));
        } else {
          showOverlay('Failed to get image URL');
        }
      } catch (err) {
        showOverlay('Failed to upload image: ' + err.message);
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handlePhoneChange = (index, field, value) => {
    const newPhones = [...formData.phoneNumbers];
    newPhones[index] = { ...newPhones[index], [field]: value };
    setFormData(prev => ({ ...prev, phoneNumbers: newPhones }));
  };

  const handleAddPhone = () => {
    setFormData(prev => ({ ...prev, phoneNumbers: [...prev.phoneNumbers, { label: 'Mobile', number: '' }] }));
  };

  const handleRemovePhone = (index) => {
    const newPhones = formData.phoneNumbers.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, phoneNumbers: newPhones }));
  };

  // Fetch all customers
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${CUSTOMER_API_BASE}/getAllCustomer`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });
      if (!response.ok) throw new Error('Failed to fetch customers');
      const data = await response.json();
      setCustomers(Array.isArray(data) ? data : data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customer details
  const fetchCustomerDetails = async (customerId) => {
    try {
      setLoadingDetail(true);
      const response = await fetch(`${CUSTOMER_API_BASE}/getCustomer/${customerId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });
      if (!response.ok) throw new Error('Failed to fetch customer details');
      const data = await response.json();
      console.log('Customer Details:', data);

      const planInvRes = await fetch(`https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/plansInvoices/getPlanInvoices`, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });
      let customerPlanInvoices = [];
      if(planInvRes.ok){
         const planInvData = await planInvRes.json();
         const allPlanInvoices = Array.isArray(planInvData) ? planInvData : (planInvData.data || []);
         customerPlanInvoices = allPlanInvoices.filter(inv => {
           const invCustomerId = typeof inv.customerId === 'object' ? inv.customerId?._id : inv.customerId;
           return String(invCustomerId) === String(customerId);
         });
      }

      const bookInvRes = await fetch(`https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookingInvoice/getAllBookingInvoice`, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });
      let customerBookingInvoices = [];
      if(bookInvRes.ok){
         const bookInvData = await bookInvRes.json();
         const allBookingInvoices = Array.isArray(bookInvData) ? bookInvData : (bookInvData.data || []);
         customerBookingInvoices = allBookingInvoices.filter(inv => inv.customer_id === customerId);
      }

      let customerTransactions = [];
      try {
        const transRes = await fetch('https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/benefitTransactions/getCustomerTransactions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ customerId: customerId })
        });
        if (transRes.ok) {
          const transData = await transRes.json();
          customerTransactions = transData.data || transData.transactions || transData || [];
        }
      } catch (error) {}

      setSelectedCustomerDetail({
        ...data,
        plan_invoices: customerPlanInvoices,
        booking_invoices: customerBookingInvoices,
        benefit_transactions: customerTransactions
      });
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      showOverlay('Failed to fetch customer details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Fetch all plans
  const fetchPlans = async () => {
    try {
      const response = await fetch(`${PLANS_API_BASE}/getPlan`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });
      if (!response.ok) throw new Error('Failed to fetch plans');
      const data = await response.json();
      setPlans(Array.isArray(data) ? data : data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchPlans();
    fetchAllProducts();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, memberFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAllProducts = async () => {
    try {
      const response = await fetch(`${PRODUCTS_API_BASE}/getProducts`, {
        headers: { Accept: 'application/json', ...getAuthHeaders() }
      });
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        setAllProducts(Array.isArray(data.data) ? data.data : []);
      }
    } catch (error) {
      console.error('Error fetching all products:', error);
    }
  };

  const showOverlay = (message) => {
    setOverlayMessage(message);
    setIsOverlayOpen(true);
    setTimeout(() => setIsOverlayOpen(false), 3000);
  };

  const handleAssignProduct = async (productId) => {
    try {
      const response = await fetch(`${PRODUCTS_API_BASE}/asignProductToCustomer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: selectedCustomerDetail.customer.id, productId: productId })
      });
      if (!response.ok) throw new Error('Failed to assign product');
      showOverlay('Product assigned successfully!');
      fetchCustomerDetails(selectedCustomerDetail.customer.id);
    } catch (error) {
      showOverlay(error.message || 'Failed to assign product');
    }
  };

  const handleUnassignProduct = async (productId) => {
    try {
      const response = await fetch(`${PRODUCTS_API_BASE}/unassignProductFromCustomer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: selectedCustomerDetail.customer.id, productId: productId })
      });
      if (!response.ok) throw new Error('Failed to unassign product');
      showOverlay('Product unassigned successfully!');
      fetchCustomerDetails(selectedCustomerDetail.customer.id);
    } catch (error) {
      showOverlay(error.message || 'Failed to unassign product');
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const resetFormValues = () => {
    setFormData({ 
      fullName: '', email: '', customerType: 'individual', isSuspended: false, 
      country: '', city: '', zipcode: '', address: '', companyName: '',
      invoiceDescription: '', invoicePrice: '', invoiceTax: '11', invoiceTotalAmount: '', invoiceDueDate: '', profilePicture: '',
      phoneNumbers: [{ label: 'Mobile', number: '' }], payment_method: 'Cash', bank_name: '', bank_code: '', sort_code: '', account_number: '',
      selectedPlans: []
    });
  };

  const handleAddCustomer = async (e) => {
    if (e) e.preventDefault();
    if (isAddingCustomer) return; // Prevent double submissions

    if (!formData.fullName || !formData.email) return showOverlay('Name and email are required');
    if (!isValidEmail(formData.email)) return showOverlay('Please enter a valid email address');
    
    // Only strictly require invoice fields if the user has actually started filling them out
    const isGeneratingInvoice = formData.selectedPlans.length > 0 && (formData.invoicePrice || formData.invoiceDueDate || formData.invoiceDescription);
    if (isGeneratingInvoice) {
      if (!formData.invoiceDescription || !formData.invoicePrice || !formData.invoiceDueDate) {
        return showOverlay('Invoice description, price, and due date are required to generate the plan invoice.');
      }
    }

    try {
      setIsAddingCustomer(true);
      const validPlansFormatted = formatPlansForApi(formData.selectedPlans.filter(p => p.planId !== ''));

      const customerResponse = await fetch(`${CUSTOMER_API_BASE}/addCustomer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          plans: validPlansFormatted,
          customerType: formData.customerType,
          isSuspended: formData.isSuspended,
          customer_country: formData.country,
          customer_city: formData.city,
          customer_zipcode: formData.zipcode,
          customer_address: formData.address,
          companyName: formData.companyName,
          profilePicture: formData.profilePicture,
          phoneNumbers: formData.phoneNumbers.filter(p => p.number && p.number.trim() !== ''),
          payment_method: formData.payment_method,
          ...(formData.payment_method === 'Bank Transfer' ? {
            bank_name: formData.bank_name,
            bank_code: formData.bank_code,
            sort_code: formData.sort_code,
            account_number: formData.account_number,
          } : {})
        })
      });

      if (!customerResponse.ok) {
        const errorData = await customerResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add customer');
      }

      // Automatically generate invoice if a plan was added AND invoice details were provided
      if (validPlansFormatted.length > 0 && isGeneratingInvoice) {
        const customerData = await customerResponse.json();
        const createdCustomer = customerData.customer || customerData.data || customerData;
        const createdCustomerId = createdCustomer._id || createdCustomer.id;

        if (createdCustomerId) {
          await fetch(`${PLAN_INVOICE_API_BASE}/createPlanInvoice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({
              planId: validPlansFormatted[0].planId, 
              customerId: createdCustomerId,
              description: formData.invoiceDescription,
              price: Number(formData.invoicePrice),
              tax: formData.invoiceTax || '0',
              totalAmount: Number(formData.invoiceTotalAmount || formData.invoicePrice),
              dueDate: new Date(formData.invoiceDueDate).toISOString(),
              deposits: [], 
              bank_name: formData.bank_name,
              bank_code: formData.bank_code,
              account_number: formData.account_number,
              sort_code: formData.sort_code
            })
          });
        }
      }

      await fetchCustomers();
      showOverlay('Customer created successfully!');
      resetFormValues();
      setShowAddModal(false);
    } catch (error) {
      showOverlay(error.message || 'Failed to add customer');
    } finally {
      setIsAddingCustomer(false);
    }
  };

  const handleEditCustomer = async () => {
    if (!formData.fullName) return showOverlay('Name is required');
    const customerId = selectedCustomerDetail.customer._id || selectedCustomerDetail.customer.id;
    const validPlansFormatted = formatPlansForApi(formData.selectedPlans.filter(p => p.planId !== ''));

    try {
      const response = await fetch(`${CUSTOMER_API_BASE}/updateCustomer/${customerId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          plans: validPlansFormatted,
          customerType: formData.customerType,
          isSuspended: formData.isSuspended,
          customer_country: formData.country,
          customer_city: formData.city,
          customer_zipcode: formData.zipcode,
          customer_address: formData.address,
          companyName: formData.companyName,
          profilePicture: formData.profilePicture,
          phoneNumbers: formData.phoneNumbers.filter(p => p.number && p.number.trim() !== ''),
          payment_method: formData.payment_method,
          ...(formData.payment_method === 'Bank Transfer' ? {
            bank_name: formData.bank_name,
            bank_code: formData.bank_code,
            sort_code: formData.sort_code,
            account_number: formData.account_number,
          } : {})
        })
      });

      if (!response.ok) throw new Error('Failed to update customer');

      await fetchCustomers();
      await fetchCustomerDetails(customerId);
      setIsEditingDetails(false);
      showOverlay('Customer updated successfully!');
    } catch (error) {
      showOverlay(error.message || 'Failed to update customer');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      const response = await fetch(`${CUSTOMER_API_BASE}/deleteCustomer/${customerId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });
      if (!response.ok) throw new Error('Failed to delete customer');
      await fetchCustomers();
      setOpenMenuId(null);
      showOverlay('Customer deleted successfully!');
    } catch (error) {
      showOverlay(error.message || 'Failed to delete customer');
    }
  };

  // Inline Plan Assignment inside Plan Tab
  const handleInlineSubmitPlan = async () => {
    if (!inlinePlanData.planId) return showOverlay('Please select a plan');
    try {
      const customerId = selectedCustomerDetail.customer._id || selectedCustomerDetail.customer.id;
      const currentPlans = selectedCustomerDetail.plans || [];
          
      const newPlanArray = [...currentPlans, inlinePlanData];
      const validPlansFormatted = formatPlansForApi(newPlanArray);

      const response = await fetch(`${CUSTOMER_API_BASE}/updateCustomer/${customerId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          plans: validPlansFormatted, 
          isMember: true
        })
      });

      if (!response.ok) throw new Error('Failed to assign new plan');

      showOverlay('Plan assigned successfully!');
      setShowInlineAddPlan(false);
      setInlinePlanData({ planId: '', quantity: 1, startDate: '', endDate: '', purchase_price: '', status: 'active' });
      fetchCustomerDetails(customerId);
      fetchCustomers();
    } catch (err) {
      showOverlay(err.message);
    }
  };

  // --- PLAN PRICE UPDATE HANDLER ---
  const handleUpdatePlanPrice = async (planIdToUpdate) => {
    if (editPurchasePrice === '') return showOverlay("Purchase price is required.");
    try {
      const customerId = selectedCustomerDetail.customer.id || selectedCustomerDetail.customer._id;
      const response = await fetch(`${CUSTOMER_API_BASE}/updatePlanPrice`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId: selectedCustomerDetail.customer.id || selectedCustomerDetail.customer._id,
          contractId: planIdToUpdate,
          purchase_price: Number(editPurchasePrice),
          status: inlinePlanData.status || 'active'
        })
      });
      
      if (!response.ok) throw new Error('Failed to update plan price');
      
      showOverlay('Plan price updated successfully!');
      setEditingPlanId(null);
      fetchCustomerDetails(customerId); 
      fetchCustomers(); 
    } catch (error) {
      showOverlay(error.message || 'Failed to update plan price');
    }
  };

  // --- DEPOSITS HANDLERS ---
  const handleOpenDepositModal = (plan, depositToEdit = null) => {
    setActivePlanForDeposit(plan);
    if(depositToEdit) {
      setIsEditingDeposit(true);
      setDepositFormData({ 
        depositId: depositToEdit._id || depositToEdit.id, 
        productId: depositToEdit.productId || '', 
        purchase_price: depositToEdit.purchase_price || '' 
      });
    } else {
      setIsEditingDeposit(false);
      setDepositFormData({ depositId: '', productId: '', purchase_price: '' });
    }
    setShowDepositModal(true);
  };

  const handleProductSelectForDeposit = (e) => {
    const pId = e.target.value;
    const prod = allProducts.find(p => p._id === pId || p.id === pId);
    setDepositFormData({ 
      ...depositFormData, 
      productId: pId, 
      purchase_price: prod ? (prod.pricing?.unit_price || prod.price || 0) : '' 
    });
  };

  const handleSubmitDeposit = async () => {
    if(!depositFormData.productId || !depositFormData.purchase_price) {
      return showOverlay("Product and Price are required.");
    }
    const customerId = selectedCustomerDetail.customer.id || selectedCustomerDetail.customer._id;
    try {
      let url = `${DEPOSITS_API_BASE}/addDeposit`;
      let payload = { 
        customerId, 
        planId: activePlanForDeposit.planId || activePlanForDeposit._id || activePlanForDeposit.id, 
        productId: depositFormData.productId, 
        purchase_price: Number(depositFormData.purchase_price) 
      };
      
      if(isEditingDeposit) {
        url = `${DEPOSITS_API_BASE}/updateDeposit`;
        payload = { 
          depositId: depositFormData.depositId, 
          purchase_price: Number(depositFormData.purchase_price) 
        };
      }

      const response = await fetch(url, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, 
        body: JSON.stringify(payload) 
      });
      
      if(!response.ok) throw new Error("Failed to save deposit");

      showOverlay(`Deposit ${isEditingDeposit ? 'updated' : 'added'} successfully`);
      setShowDepositModal(false);
      fetchCustomerDetails(customerId);
    } catch (e) { 
      showOverlay(e.message); 
    }
  };

  const handleDeleteDeposit = async (depositId) => {
    if(!window.confirm("Delete this deposit?")) return;
    try {
      const response = await fetch(`${DEPOSITS_API_BASE}/deleteDeposit`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, 
        body: JSON.stringify({ depositId }) 
      });
      if(!response.ok) throw new Error("Failed to delete deposit");
      showOverlay("Deposit deleted successfully");
      fetchCustomerDetails(selectedCustomerDetail.customer.id || selectedCustomerDetail.customer._id);
    } catch(e) { 
      showOverlay(e.message); 
    }
  };

  // --- MANUAL BENEFITS HANDLERS ---
  const handleAddManualCredit = async () => {
    if(!creditFormData.amount) return showOverlay("Amount is required");
    const customerId = selectedCustomerDetail.customer.id || selectedCustomerDetail.customer._id;
    
    try {
      const payload = {
        customerId, 
        type: creditFormData.type, 
        amount: Number(creditFormData.amount), 
        expireInDays: Number(creditFormData.expireInDays)
      };

      if (creditFormData.type === 'time_credit') {
        payload.unit = creditFormData.unit;
      }

      const response = await fetch(`${CUSTOMER_API_BASE}/addManualCreditToCustomer`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, 
        body: JSON.stringify(payload)
      });

      if(!response.ok) throw new Error("Failed to add credit");

      showOverlay("Credit added manually");
      setShowCreditModal(false);
      setCreditFormData({ type: 'time_credit', amount: '', unit: 'minutes', expireInDays: 30 });
      fetchCustomerDetails(customerId);
    } catch(e) { 
      showOverlay(e.message); 
    }
  };

  // --- PLAN INVOICE WITH DEPOSITS HANDLERS ---
  const handleOpenPlanInvoiceModal = (plan) => {
    const planQty = Number(plan.quantity) || 1;
    const planCost = Number(plan.customer_price || plan.purchase_price || 0) * planQty;
    
    const planDeposits = plan.deposits || [];
    const uninvoicedDeposits = planDeposits.filter(dep => !dep.isInvoice);

    const formattedDeposits = uninvoicedDeposits.map(dep => {
      const dPrice = Number(dep.purchase_price || 0);
      const productName = allProducts.find(ap => ap._id === dep.productId || ap.id === dep.productId)?.name || dep.product_name || "Deposit Product";
      return {
        depositId: dep.id || dep._id, 
        productName, 
        price: dPrice, 
        quantity: planQty, 
        total: dPrice * planQty 
      };
    });

    let depositTotal = 0;
    formattedDeposits.forEach(d => depositTotal += d.total);
    
    const taxValue = 11;
    const finalTotal = planCost + (planCost * taxValue / 100) + depositTotal;

    const bankObj = selectedCustomerDetail.bank_details || {};

    const actualPlanId = (typeof plan.planId === 'object' ? plan.planId._id : plan.planId) || plan._id || plan.id;

    setInvoiceFormData({
      planId: actualPlanId, 
      description: `${plan.name || 'Plan'} Subscription`, 
      price: planCost, 
      tax: String(taxValue), 
      totalAmount: finalTotal, 
      dueDate: '', 
      deposits: formattedDeposits,
      bank_name: bankObj.bank_name || '',
      bank_code: bankObj.bank_code || '',
      account_number: bankObj.account_number || '',
      sort_code: bankObj.sort_code || ''
    });
    setShowPlanInvoiceModal(true);
  };

  const handleInvoiceChange = (field, value) => {
    setInvoiceFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'price' || field === 'tax') {
        const basePrice = Number(updated.price) || 0;
        let depositTotal = 0;
        updated.deposits.forEach(d => depositTotal += d.total);
        const tRate = Number(updated.tax) || 0;
        updated.totalAmount = basePrice + (basePrice * tRate / 100) + depositTotal;
      }
      return updated;
    });
  };

  const handleCreatePlanInvoice = async () => {
    if(!invoiceFormData.dueDate || !invoiceFormData.price) return showOverlay("Price and Due Date are required");
    const customerId = selectedCustomerDetail.customer.id || selectedCustomerDetail.customer._id;
    console.log("Creating invoice with data:", {
      customerId, 
      planId: invoiceFormData.planId,
      description: invoiceFormData.description,
      price: invoiceFormData.price,
      tax: invoiceFormData.tax,
      totalAmount: invoiceFormData.totalAmount,
      dueDate: invoiceFormData.dueDate,
      deposits: invoiceFormData.deposits
    });
    try {
      const response = await fetch(`${PLAN_INVOICE_API_BASE}/createPlanInvoice`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId, 
          planId: invoiceFormData.planId, 
          description: invoiceFormData.description, 
          price: Number(invoiceFormData.price),
          tax: invoiceFormData.tax, 
          totalAmount: Number(invoiceFormData.totalAmount), 
          dueDate: new Date(invoiceFormData.dueDate).toISOString(),
          deposits: invoiceFormData.deposits,
          bank_name: invoiceFormData.bank_name,
          bank_code: invoiceFormData.bank_code,
          account_number: invoiceFormData.account_number,
          sort_code: invoiceFormData.sort_code
        })
      });
      if(!response.ok) throw new Error("Failed to create Plan Invoice");
      showOverlay("Invoice Generated Successfully!");
      setShowPlanInvoiceModal(false);
      fetchCustomerDetails(customerId);
    } catch(e) { 
      showOverlay(e.message); 
    }
  };

  const handleRowClick = (customer, event) => {
    if (event.target.closest('.actions-menu')) return;
    fetchCustomerDetails(customer._id || customer.id);
  };

  const handleExport = () => {
    const headers = ['Full Name', 'Email', 'Member Status', 'Suspended', 'Created At'];
    const rows = filteredCustomers.map(c => [
      c.fullName, c.email, c.isMember ? 'Member' : 'Contacts', c.isSuspended ? 'Yes' : 'No', new Date(c.createdAt).toLocaleDateString()
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customers.csv';
    a.click();
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ? true : statusFilter === 'active' ? !customer.isSuspended : statusFilter === 'suspended' ? customer.isSuspended : true;
    const matchesMember = memberFilter === 'all' ? true : memberFilter === 'member' ? customer.isMember : memberFilter === 'Contacts' ? !customer.isMember : true;
    return matchesSearch && matchesStatus && matchesMember;
  });

  //  PAGINATION LOGIC 
  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

  const totalCustomers = customers.length;
  const activeMembers = customers.filter(c => c.isMember && !c.isSuspended).length;
  const suspendedCustomers = customers.filter(c => c.isSuspended === true).length;
  const nonMembers = customers.filter(c => !c.isMember).length;

  const getPlanName = (customerPlans) => {
    if (!customerPlans || customerPlans.length === 0) return 'N/A';
    const plan = plans.find(p => p._id === customerPlans[0].planId);
    return plan?.name || 'N/A';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loadingDetail || loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <Sidebar currentPath="/customers" />
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto bg-gray-50 flex items-center justify-center">
            <div className="text-gray-500">Loading...</div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar 
        currentPath="/customers" 
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* ── Mobile top bar ── */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 sticky top-0 z-30">
          <button
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <img
            src="/logo.png"
            alt="Optimus Logo"
            className="h-7 w-auto object-contain mr-5"
          />
        </header>

        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 md:p-6">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-gray-900">Customers</h1>
              <p className="text-sm text-gray-500 mt-1">Manage your customer base and memberships</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-3"><div className="text-sm text-gray-600">Total Customers</div></div>
                <div className="flex items-end justify-between"><div className="text-3xl font-semibold text-gray-900">{totalCustomers}</div><Users className="w-5 h-5 text-gray-400" /></div>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-3"><div className="text-sm text-gray-600">Members</div></div>
                <div className="flex items-end justify-between"><div className="text-3xl font-semibold text-gray-900">{activeMembers}</div><UserCheck className="w-5 h-5 text-gray-400" /></div>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-3"><div className="text-sm text-gray-600">Contacts</div></div>
                <div className="flex items-end justify-between"><div className="text-3xl font-semibold text-gray-900">{nonMembers}</div><Users className="w-5 h-5 text-gray-400" /></div>
              </div>
              <div className="bg-[#214BAE] rounded-lg p-5 shadow-sm">
                <div className="flex items-start justify-between mb-3"><div className="text-sm text-blue-100">Suspended</div></div>
                <div className="flex items-end justify-between"><div className="text-3xl font-semibold text-white">{suspendedCustomers}</div><XCircle className="w-5 h-5 text-white" /></div>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
              <div className="p-4 flex items-center justify-between gap-4 overflow-x-auto pb-2">
                <div className="flex items-center gap-3 min-w-max flex-1">
                  <div className="relative flex-1 min-w-[200px] sm:min-w-[300px] max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" placeholder="Search by name or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" />
                    {searchTerm && (
                      <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="appearance-none text-sm border border-gray-200 rounded-lg px-4 py-2 pr-10 bg-white focus:ring-2 focus:ring-blue-500">
                      <option value="all">All Status</option><option value="active">Active</option><option value="suspended">Suspended</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                  </div>
                  <div className="relative">
                    <select value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)} className="appearance-none text-sm border border-gray-200 rounded-lg px-4 py-2 pr-10 bg-white focus:ring-2 focus:ring-blue-500">
                      <option value="all">All Types</option><option value="member">Members</option><option value="Contacts">Contacts</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
                <div className="flex items-center gap-2 min-w-max">
                  <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
                  <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-[#214BAE] rounded-lg hover:bg-blue-700"><UserPlus className="w-4 h-4" /> Add Customer</button>
                </div>
              </div>
            </div>

            {/* Customers Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
              <div className="min-w-[1000px]">
                <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentCustomers.length > 0 ? currentCustomers.map((customer) => (
                    <tr key={customer._id || customer.id} onClick={(e) => handleRowClick(customer, e)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                      <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{customer.fullName}</div></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">{customer.email}</div></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">{getPlanName(customer.plans)}</div></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900 capitalize">{customer.customerType || 'Individual'}</div></td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${customer.isMember ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {customer.isMember ? 'Member' : 'Contacts'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${customer.isSuspended ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                          {customer.isSuspended ? 'Suspended' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">{new Date(customer.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div></td>
                      <td className="px-6 py-4 whitespace-nowrap text-right relative">
                        <div className="relative inline-block actions-menu" ref={openMenuId === (customer._id || customer.id) ? menuRef : null}>
                          <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === (customer._id || customer.id) ? null : (customer._id || customer.id)); }} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <MoreVertical className="w-5 h-5" />
                          </button>
                          {openMenuId === (customer._id || customer.id) && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                              <button onClick={(e) => { e.stopPropagation(); fetchCustomerDetails(customer._id || customer.id); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                <Eye className="w-4 h-4" /> View / Edit Customer
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); handleDeleteCustomer(customer._id || customer.id); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                <Trash2 className="w-4 h-4" /> Delete Customer
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : <tr><td colSpan="8" className="px-6 py-12 text-center text-sm text-gray-500">No customers found</td></tr>}
                </tbody>
              </table>
            </div>
            </div>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-between px-4 py-3 bg-white border border-t-0 border-gray-200 rounded-b-lg sm:px-6 mt-0">
                <div className="hidden sm:block">
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstCustomer + 1}</span> to <span className="font-medium">{Math.min(indexOfLastCustomer, filteredCustomers.length)}</span> of <span className="font-medium">{filteredCustomers.length}</span> results
                  </p>
                </div>
                <div className="flex justify-between flex-1 sm:justify-end gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <div className="flex items-center px-4 text-sm font-medium text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[55] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Add New Customer</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetFormValues();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto max-h-[500px]" >
              {/* Profile Picture Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploadingImage}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {uploadingImage && <div className="mt-1 text-sm text-blue-600 animate-pulse">Uploading image...</div>}
                {formData.profilePicture && !uploadingImage && (
                  <img src={formData.profilePicture} alt="Preview" className="mt-2 w-16 h-16 object-cover rounded-full border border-gray-300" />
                )}
              </div>

              {/* Phone Numbers */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Phone Numbers</label>
                  <button type="button" onClick={handleAddPhone} className="text-xs text-blue-600 flex items-center hover:underline">
                    <Plus className="w-3 h-3 mr-1"/> Add Phone
                  </button>
                </div>
                {formData.phoneNumbers.map((phone, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <select
                      value={phone.label}
                      onChange={(e) => handlePhoneChange(index, 'label', e.target.value)}
                      className="w-1/3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Mobile">Mobile</option>
                      <option value="Work">Work</option>
                      <option value="Home">Home</option>
                    </select>
                    <input
                      type="text"
                      value={phone.number}
                      onChange={(e) => handlePhoneChange(index, 'number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter phone number"
                    />
                    {formData.phoneNumbers.length > 1 && (
                      <button type="button" onClick={() => handleRemovePhone(index)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg">
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input type="text" value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter full name" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter email address" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                <input type="text" value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter company name" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <input type="text" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter country" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter city" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zipcode</label>
                <input type="text" value={formData.zipcode} onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter zipcode" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter address" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Member Status</label>
                <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed font-medium">
                  {formData.selectedPlans.length > 0 ? 'Member' : 'Contacts'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Type</label>
                <div className="relative">
                  <select value={formData.customerType} onChange={(e) => setFormData({ ...formData, customerType: e.target.value })} className="w-full appearance-none px-4 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="individual">Individual</option>
                    <option value="corporate">Corporate</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Status</label>
                <div className="relative">
                  <select value={formData.isSuspended} onChange={(e) => setFormData({ ...formData, isSuspended: e.target.value === 'true' })} className="w-full appearance-none px-4 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="false">Active</option>
                    <option value="true">Suspended</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                <div className="relative">
                  <select value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} className="w-full appearance-none px-4 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {/* Conditional Bank Details */}
              {formData.payment_method === 'Bank Transfer' && (
                <div className="p-4 mt-2 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Bank Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Bank Name</label>
                      <input type="text" value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="e.g. OCBC NISP" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Account Number</label>
                      <input type="text" value={formData.account_number} onChange={(e) => setFormData({ ...formData, account_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Bank Code</label>
                      <input type="text" value={formData.bank_code} onChange={(e) => setFormData({ ...formData, bank_code: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Sort Code</label>
                      <input type="text" value={formData.sort_code} onChange={(e) => setFormData({ ...formData, sort_code: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                    </div>
                  </div>
                </div>
              )}

              {/* Plans Array Editor */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-900">Assign Plans</label>
                  <button type="button" onClick={handleAddSelectedPlan} className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-lg flex items-center hover:bg-blue-100">
                    <Plus className="w-3 h-3 mr-1"/> Add Plan
                  </button>
                </div>
                {formData.selectedPlans.map((sp, index) => (
                  <div key={index} className="p-4 bg-white border border-gray-200 rounded-lg mb-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-semibold text-gray-700">Plan #{index + 1}</h4>
                      <button type="button" onClick={() => handleRemoveSelectedPlan(index)} className="text-red-500 hover:bg-red-50 p-1 rounded">
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Select Plan</label>
                        <select value={sp.planId} onChange={(e) => handleSelectedPlanChange(index, 'planId', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500">
                          <option value="">Select a plan</option>
                          {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Price (IDR)</label>
                        <input type="number" value={sp.purchase_price} onChange={(e) => handleSelectedPlanChange(index, 'purchase_price', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                        <input type="number" min="1" value={sp.quantity} onChange={(e) => handleSelectedPlanChange(index, 'quantity', parseInt(e.target.value) || 1)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                        <input type="date" value={sp.startDate} onChange={(e) => handleSelectedPlanChange(index, 'startDate', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                        <input type="date" value={sp.endDate} onChange={(e) => handleSelectedPlanChange(index, 'endDate', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                        <select value={sp.status} onChange={(e) => handleSelectedPlanChange(index, 'status', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500">
                          <option value="active">Active</option>
                          <option value="suspended">Suspended</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
                {formData.selectedPlans.length === 0 && <p className="text-sm text-gray-500 italic">No plans assigned.</p>}
              </div>

              {formData.selectedPlans.length > 0 && (
                <div className="p-4 mt-4 bg-blue-50 border border-blue-100 rounded-lg space-y-4">
                  <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Plan Invoice Details
                  </h3>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Description *</label>
                    <textarea value={formData.invoiceDescription} onChange={(e) => handleInvoiceFieldChange('invoiceDescription', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" rows="2" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Price *</label>
                      <input type="number" value={formData.invoicePrice} onChange={(e) => handleInvoiceFieldChange('invoicePrice', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Tax (%)</label>
                      <input type="number" value={formData.invoiceTax} onChange={(e) => handleInvoiceFieldChange('invoiceTax', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Total Amount</label>
                      <input type="number" value={formData.invoiceTotalAmount} readOnly className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-gray-100 text-gray-600 cursor-not-allowed" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Due Date *</label>
                      <input type="date" value={formData.invoiceDueDate} onChange={(e) => handleInvoiceFieldChange('invoiceDueDate', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetFormValues();
                }}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCustomer}
                disabled={isAddingCustomer}
                className="flex items-center justify-center px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isAddingCustomer ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  'Add Customer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Detail Modal */}
      {showDetailModal && selectedCustomerDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-6 pb-2">
              <div className="flex items-center gap-4">
                {selectedCustomerDetail.customer?.profilePicture ? (
                  <img src={selectedCustomerDetail.customer.profilePicture} alt="Profile" className="w-14 h-14 rounded-full object-cover border border-gray-200" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xl">
                    {selectedCustomerDetail.customer?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedCustomerDetail.customer?.name || selectedCustomerDetail.customer?.fullName}</h2>
                  <p className="text-sm text-gray-500 mt-1">{selectedCustomerDetail.customer?.email}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedCustomerDetail(null);
                  setActiveDetailTab('Customer Information');
                  setIsEditingDetails(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex px-6 border-b border-gray-200 overflow-x-auto hide-scrollbar">
              {[
                { name: 'Customer Information', Icon: UserCheck },
                { name: 'Billing Info', Icon: DollarSign },
                { name: 'Booking status', Icon: Calendar },
                { name: 'Plan', Icon: CreditCard },
                { name: 'Products', Icon: Package },
                { name: 'Benefits', Icon: Gift },
                { name: 'Invoices', Icon: Download }
              ].map((tab) => (
                <button
                  key={tab.name}
                  onClick={() => setActiveDetailTab(tab.name)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium focus:outline-none whitespace-nowrap transition-colors ${
                    activeDetailTab === tab.name ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-transparent'
                  }`}
                >
                  <tab.Icon className="w-4 h-4" /> {tab.name}
                </button>
              ))}
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 bg-white">
              
              {/* 1. CUSTOMER INFORMATION TAB */}
              {activeDetailTab === 'Customer Information' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Customer Details</h3>
                    {!isEditingDetails ? (
                      <button onClick={startEditingDetails} className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" /> Edit
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => setIsEditingDetails(false)} className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                        <button onClick={handleEditCustomer} className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">Save</button>
                      </div>
                    )}
                  </div>

                  {!isEditingDetails ? (
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Basic Details</h3>
                        <div className="bg-gray-50 p-4 rounded-lg space-y-5">
                          <div><p className="text-xs text-gray-500">Customer Type</p><p className="text-sm font-medium text-gray-900 capitalize">{selectedCustomerDetail.customer?.customerType || 'N/A'}</p></div>
                          <div>
                            <p className="text-xs text-gray-500">Member Status</p>
                            <span className={`inline-flex px-2 py-0.5 mt-1 text-xs font-medium rounded-full ${selectedCustomerDetail.customer?.isMember ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {selectedCustomerDetail.customer?.isMember ? 'Member' : 'Contact'}
                            </span>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Account Status</p>
                            <span className={`inline-flex px-2 py-0.5 mt-1 text-xs font-medium rounded-full ${selectedCustomerDetail.customer?.isSuspended ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                              {selectedCustomerDetail.customer?.isSuspended ? 'Suspended' : 'Active'}
                            </span>
                          </div>
                          <div><p className="text-xs text-gray-500">Created At</p><p className="text-sm font-medium text-gray-900">{new Date(selectedCustomerDetail.customer?.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</p></div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Contact Details</h3>
                        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                          {selectedCustomerDetail.customer?.phoneNumbers?.length > 0 ? (
                            <div className="pb-2 border-b border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">Phone Numbers</p>
                              {selectedCustomerDetail.customer.phoneNumbers.map((phone, index) => (
                                <p key={index} className="text-sm font-medium text-gray-900"><span className="text-gray-500 mr-2">{phone.label}:</span> {phone.number}</p>
                              ))}
                            </div>
                          ) : (
                            <div className="pb-2 border-b border-gray-200"><p className="text-xs text-gray-500 mb-1">Phone Numbers</p><p className="text-sm text-gray-500">N/A</p></div>
                          )}
                          <div className="pt-2 border-gray-200"><p className="text-xs text-gray-500 mb-1">Company Name</p><p className="text-sm font-medium text-gray-900">{selectedCustomerDetail.customer?.companyName || 'N/A'}</p></div>
                          <div className="pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-500 mb-1">Address</p>
                            {selectedCustomerDetail.address?.address ? (
                              <p className="text-sm font-medium text-gray-900">
                                {selectedCustomerDetail.address.address}
                                {selectedCustomerDetail.address.city && `, ${selectedCustomerDetail.address.city}`}
                                {selectedCustomerDetail.address.zipcode && ` - ${selectedCustomerDetail.address.zipcode}`}
                                {selectedCustomerDetail.address.country && `, ${selectedCustomerDetail.address.country}`}
                              </p>
                            ) : <p className="text-sm text-gray-500">N/A</p>}
                          </div>
                        </div>
                      </div>

                      {/* Payment Details */}
                      <div className="col-span-2 mt-2">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Payment Details</h3>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="mb-4">
                            <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                            <span className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {selectedCustomerDetail.customer?.payment_method || 'Cash'}
                            </span>
                          </div>
                          {selectedCustomerDetail.customer?.payment_method === 'Bank Transfer' && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                              <div><p className="text-xs text-gray-500 mb-1">Bank Name</p><p className="text-sm font-medium text-gray-900">{selectedCustomerDetail.bank_details?.bank_name || 'N/A'}</p></div>
                              <div><p className="text-xs text-gray-500 mb-1">Account Number</p><p className="text-sm font-medium text-gray-900">{selectedCustomerDetail.bank_details?.account_number || 'N/A'}</p></div>
                              <div><p className="text-xs text-gray-500 mb-1">Bank Code</p><p className="text-sm font-medium text-gray-900">{selectedCustomerDetail.bank_details?.bank_code || 'N/A'}</p></div>
                              <div><p className="text-xs text-gray-500 mb-1">Sort Code</p><p className="text-sm font-medium text-gray-900">{selectedCustomerDetail.bank_details?.sort_code || 'N/A'}</p></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // --- EDIT MODE ---
                    <div className="grid grid-cols-2 gap-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {/* Form Details */}
                      <div><label className="block text-xs font-medium text-gray-700 mb-1">Full Name</label><input type="text" value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div><label className="block text-xs font-medium text-gray-700 mb-1">Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div><label className="block text-xs font-medium text-gray-700 mb-1">Company Name</label><input type="text" value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Customer Type</label>
                        <select value={formData.customerType} onChange={(e) => setFormData({ ...formData, customerType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="individual">Individual</option>
                          <option value="corporate">Corporate</option>
                        </select>
                      </div>
                      <div><label className="block text-xs font-medium text-gray-700 mb-1">Address</label><input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div><label className="block text-xs font-medium text-gray-700 mb-1">City</label><input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div><label className="block text-xs font-medium text-gray-700 mb-1">Zipcode</label><input type="text" value={formData.zipcode} onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div><label className="block text-xs font-medium text-gray-700 mb-1">Country</label><input type="text" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Account Status</label>
                        <select value={formData.isSuspended} onChange={(e) => setFormData({ ...formData, isSuspended: e.target.value === 'true' })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="false">Active</option>
                          <option value="true">Suspended</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Payment Method</label>
                        <select value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="Cash">Cash</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                        </select>
                      </div>

                      {formData.payment_method === 'Bank Transfer' && (
                        <div className="col-span-2 p-4 border border-gray-200 rounded-lg bg-white space-y-4">
                          <h3 className="text-sm font-semibold text-gray-900">Bank Details</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-xs font-medium text-gray-700 mb-1">Bank Name</label><input type="text" value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                            <div><label className="block text-xs font-medium text-gray-700 mb-1">Account Number</label><input type="text" value={formData.account_number} onChange={(e) => setFormData({ ...formData, account_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                            <div><label className="block text-xs font-medium text-gray-700 mb-1">Bank Code</label><input type="text" value={formData.bank_code} onChange={(e) => setFormData({ ...formData, bank_code: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                            <div><label className="block text-xs font-medium text-gray-700 mb-1">Sort Code</label><input type="text" value={formData.sort_code} onChange={(e) => setFormData({ ...formData, sort_code: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                          </div>
                        </div>
                      )}

                      <div className="col-span-2">
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-xs font-medium text-gray-700">Phone Numbers</label>
                          <button type="button" onClick={handleAddPhone} className="text-xs text-blue-600 hover:underline flex items-center">
                            <Plus className="w-3 h-3 mr-1" /> Add Phone
                          </button>
                        </div>
                        {formData.phoneNumbers.map((phone, index) => (
                          <div key={index} className="flex gap-2 mb-2">
                            <select value={phone.label} onChange={(e) => handlePhoneChange(index, 'label', e.target.value)} className="w-1/3 px-3 py-2 border border-gray-300 rounded-lg text-sm">
                              <option value="Mobile">Mobile</option>
                              <option value="Work">Work</option>
                              <option value="Home">Home</option>
                            </select>
                            <input type="text" value={phone.number} onChange={(e) => handlePhoneChange(index, 'number', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Phone number" />
                            {formData.phoneNumbers.length > 1 && (
                              <button type="button" onClick={() => handleRemovePhone(index)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4"/></button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 2. BILLING INFO TAB */}
              {activeDetailTab === 'Billing Info' && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-600 mb-1">Plan Price</p>
                    <p className="text-2xl font-semibold text-blue-900">
                      IDR {formatCurrency(selectedCustomerDetail.billing?.plans_total_price || 0)}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                    <p className="text-xs text-purple-600 mb-1">Products Total</p>
                    <p className="text-2xl font-semibold text-purple-900">
                      IDR {formatCurrency(selectedCustomerDetail.billing?.products_total_price || 0)}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                    <p className="text-xs text-green-600 mb-1">Total Revenue</p>
                    <p className="text-2xl font-semibold text-green-900">
                      IDR {formatCurrency(selectedCustomerDetail.billing?.total_revenue || 0)}
                    </p>
                  </div>
                </div>
              )}

              {/* 3. BOOKING STATUS TAB */}
              {activeDetailTab === 'Booking status' && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Total Bookings</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {selectedCustomerDetail.booking_stats?.total_bookings || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-xs text-green-600 mb-1">Active Bookings</p>
                    <p className="text-2xl font-semibold text-green-900">
                      {selectedCustomerDetail.booking_stats?.active_bookings || 0}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <p className="text-xs text-red-600 mb-1">Cancelled</p>
                    <p className="text-2xl font-semibold text-red-900">
                      {selectedCustomerDetail.booking_stats?.cancelled_bookings || 0}
                    </p>
                  </div>
                </div>
              )}

              {/* 4. PLAN TAB */}
              {activeDetailTab === 'Plan' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Assigned Plans & Deposits</h3>
                    <button onClick={() => setShowInlineAddPlan(true)} className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                      <Plus className="w-4 h-4" /> Add Plan
                    </button>
                  </div>

                  {showInlineAddPlan && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg shadow-sm space-y-4">
                      <h4 className="text-sm font-semibold text-blue-900">Assign New Plan</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Select Plan</label>
                          <select 
                            value={inlinePlanData.planId} 
                            onChange={(e) => {
                              const val = e.target.value;
                              const selectedPlan = plans.find(p => p._id === val);
                              const rawPrice = selectedPlan ? (typeof selectedPlan.price === 'string' ? parseFloat(selectedPlan.price.replace(/[^0-9.-]+/g, "")) : selectedPlan.price) : '';
                              setInlinePlanData({ ...inlinePlanData, planId: val, purchase_price: rawPrice });
                            }} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select a plan</option>
                            {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Price (IDR)</label>
                          <input type="number" value={inlinePlanData.purchase_price} onChange={(e) => setInlinePlanData({ ...inlinePlanData, purchase_price: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                          <input type="number" min="1" value={inlinePlanData.quantity} onChange={(e) => setInlinePlanData({ ...inlinePlanData, quantity: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                          <input type="date" value={inlinePlanData.startDate} onChange={(e) => setInlinePlanData({ ...inlinePlanData, startDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                          <input type="date" value={inlinePlanData.endDate} onChange={(e) => setInlinePlanData({ ...inlinePlanData, endDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                          <select value={inlinePlanData.status} onChange={(e) => setInlinePlanData({ ...inlinePlanData, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500">
                            <option value="active">Active</option>
                            <option value="suspended">Suspended</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => { setShowInlineAddPlan(false); setInlinePlanData({ planId: '', quantity: 1, purchase_price: '', startDate: '', endDate: '', status: 'active' }); }} className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                        <button onClick={handleInlineSubmitPlan} className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">Save Plan</button>
                      </div>
                    </div>
                  )}

                  {(() => {
                    const plansList = selectedCustomerDetail.plans || [];
                      
                    if (plansList.length === 0) return (
                      <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <CreditCard className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No active plans assigned.</p>
                      </div>
                    );

                    return plansList.map((planItem, idx) => {
                      const pData = plans.find(p => p._id === (planItem.planId || planItem._id)) || planItem;
                      const activePlanId = planItem._id || planItem.id;
                      
                      return (
                        <div key={idx} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-xs text-blue-600 font-semibold uppercase tracking-wider">Plan {idx + 1}</p>
                              <div className="flex items-center gap-3">
                                <h4 className="text-lg font-bold text-gray-900 mt-1">{pData.name || planItem.name || 'Unknown Plan'}</h4>
                                <span className={`px-3 py-1 mt-1 text-xs font-semibold rounded-full ${pData.active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  {pData.active !== false ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                            <button onClick={() => handleOpenPlanInvoiceModal(planItem)} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                              <FileText className="w-4 h-4" /> Generate Invoice
                            </button>
                          </div>
                          <div className='mb-4 mt-2'>
                            <div>
                      <p className="text-xs text-gray-500 mb-1">Private Office Number</p>
                      <p className="text-sm font-medium text-gray-900">
                        {pData?.privateOfficeNo || 'N/A'}
                      </p>
                    </div>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-6 gap-6 mb-6">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Base Price</p>
                              <p className="text-sm font-medium text-gray-900">IDR {formatCurrency(planItem.customer_price || pData.price || 0)}</p>
                            </div>
                            
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Purchase Price</p>
                              {editingPlanId === activePlanId ? (
                                <input 
                                  type="number" 
                                  value={editPurchasePrice} 
                                  onChange={(e) => setEditPurchasePrice(e.target.value)} 
                                  className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" 
                                />
                              ) : (
                                <p className="text-sm font-medium text-gray-900 mt-1">
                                  IDR {formatCurrency(planItem.purchase_price || planItem.customer_price || pData.price || 0)}
                                </p>
                              )}
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">Quantity</p>
                              <p className="text-sm font-medium text-gray-900 mt-1">{planItem.quantity || 1}</p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">Start Date</p>
                              <p className="text-sm font-medium text-gray-900 mt-1">{planItem.start_date || planItem.startDate ? new Date(planItem.start_date || planItem.startDate).toLocaleDateString() : 'N/A'}</p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">End Date</p>
                              <p className="text-sm font-medium text-gray-900 mt-1">{planItem.end_date || planItem.endDate ? new Date(planItem.end_date || planItem.endDate).toLocaleDateString() : 'N/A'}</p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">Status</p>
                              {editingPlanId === activePlanId ? (
                                <select 
                                  value={inlinePlanData.status || planItem.status || 'active'} 
                                  onChange={(e) => setInlinePlanData({ ...inlinePlanData, status: e.target.value })}
                                  className="w-full px-2 py-1 text-sm border border-blue-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="active">Active</option>
                                  <option value="suspended">Suspended</option>
                                </select>
                              ) : (
                                <span className={`inline-flex px-2.5 py-0.5 mt-1 text-xs font-medium rounded-full capitalize ${planItem.status === 'suspended' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                  {planItem.status || 'active'}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Edit / Save Actions Moved Below the Grid */}
                          <div className="flex justify-end mb-4">
                            {editingPlanId === activePlanId ? (
                              <div className="flex items-center gap-2">
                                <button onClick={() => setEditingPlanId(null)} className="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                                  Cancel
                                </button>
                                <button 
                                  onClick={() => handleUpdatePlanPrice(activePlanId)} 
                                  className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                                >
                                  Save Changes
                                </button>
                              </div>
                            ) : (
                              <button 
                                onClick={() => {
                                  setEditingPlanId(activePlanId);
                                  setEditPurchasePrice(planItem.purchase_price || planItem.customer_price || pData.price || 0);
                                  setInlinePlanData({ ...inlinePlanData, status: planItem.status || 'active' }); // Pre-fill status
                                }}
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                              >
                                <Edit className="w-3 h-3" /> Edit Contract Details
                              </button>
                            )}
                          </div>


                          {/* Deposits Section under each Plan */}
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex justify-between items-center mb-3">
                              <h5 className="font-semibold text-sm text-gray-800 flex items-center gap-2">
                                <Package className="w-4 h-4 text-gray-500" /> Plan Deposits (Products)
                              </h5>
                              <button onClick={() => handleOpenDepositModal(planItem)} className="flex items-center gap-1 text-xs bg-white text-gray-700 hover:text-blue-600 px-3 py-1.5 rounded border border-gray-300 shadow-sm transition-colors">
                                <Plus className="w-3 h-3" /> Add Deposit
                              </button>
                            </div>
                            
                            {planItem.deposits && planItem.deposits.length > 0 ? (
                              <div className="bg-white rounded border border-gray-200 overflow-hidden">
                                <table className="w-full text-sm text-left">
                                  <thead className="text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                                    <tr>
                                      <th className="px-4 py-2">Product Name</th>
                                      <th className="px-4 py-2">Unit Price</th>
                                      <th className="px-4 py-2 text-right">Total (Qty: {planItem.quantity || 1})</th>
                                      <th className="px-4 py-2 text-center">Invoiced</th>
                                      <th className="px-4 py-2 text-right">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {planItem.deposits.map(dep => {
                                      const depProd = allProducts.find(p => p._id === dep.productId || p.id === dep.productId);
                                      const depQty = planItem.quantity || 1;
                                      const lineTotal = Number(dep.purchase_price) * depQty;
                                      return (
                                      <tr key={dep._id || dep.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 font-medium text-gray-900">{depProd?.name || depProd?.product_name || dep?.product_name || 'Unknown Product'}</td>
                                        <td className="px-4 py-3 text-gray-600">IDR {formatCurrency(dep.purchase_price)}</td>
                                        <td className="px-4 py-3 text-gray-900 font-semibold text-right">IDR {formatCurrency(lineTotal)}</td>
                                        <td className="px-4 py-3 text-center">
                                          {dep.isInvoice ? (
                                            <span className="inline-flex items-center justify-center gap-1 text-green-600">
                                              <CheckCircle className="w-4 h-4" /> Yes
                                            </span>
                                          ) : (
                                            <span className="text-gray-400">No</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                          <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => handleOpenDepositModal(planItem, dep)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Edit Deposit"><Edit className="w-4 h-4"/></button>
                                            <button onClick={() => handleDeleteDeposit(dep._id || dep.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Delete Deposit"><Trash2 className="w-4 h-4"/></button>
                                          </div>
                                        </td>
                                      </tr>
                                    )})}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500 italic mt-2">No deposits added to this plan yet.</p>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {/* 5. PRODUCTS TAB */}
              {activeDetailTab === 'Products' && (
                <div className="space-y-8">
                  {/* Assigned Products Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4" /> Assigned General Products
                    </h3>
                    {selectedCustomerDetail.products && selectedCustomerDetail.products.length > 0 ? (
                      <div className="space-y-3">
                        {selectedCustomerDetail.products.map((product, index) => (
                          <div key={index} className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-gray-100 rounded-md">
                                <Package className="w-5 h-5 text-gray-600" />
                              </div>
                              <span className="font-medium text-gray-900">{product.name || product.product_name || 'Unnamed Product'}</span>
                            <span className="font-medium text-gray-900">IDR {formatCurrency(product.price || product.pricing?.unit_price || 0)}</span>
                            </div>
                            <button
                                onClick={() => handleUnassignProduct(product.id || product._id)}
                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100`}
                              >
                                Unassign
                              </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-sm text-gray-500">No general products assigned to this customer.</p>
                      </div>
                    )}
                  </div>

                  {/* Available Products Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Available Products
                    </h3>
                    {allProducts && allProducts.length > 0 ? (
                      <div className="space-y-3">
                        {allProducts.map((product) => {
                          const isAssigned = selectedCustomerDetail.products?.some(p => p._id === product._id || p.id === product._id);
                          return (
                            <div key={product._id} className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-50 rounded-md">
                                  <Package className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{product.name || product.product_name}</p>
                                  <p className="text-xs text-gray-500">IDR {formatCurrency(product.pricing?.unit_price || 0)}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleAssignProduct(product._id)}
                                disabled={isAssigned}
                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                  isAssigned 
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                }`}
                              >
                                {isAssigned ? 'Assigned' : 'Assign to Customer'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-sm text-gray-500">No available products found.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 6. BENEFITS TAB */}
              {activeDetailTab === 'Benefits' && (
                <div className="space-y-8">
                  {/* Benefits Summary Section */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <Gift className="w-4 h-4" /> Benefits Summary
                      </h3>
                      <button onClick={() => setShowCreditModal(true)} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                        <Plus className="w-4 h-4" /> Add Manual Credit
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <p className="text-xs text-yellow-600 mb-1">Money Credit</p>
                        <p className="text-xl font-semibold text-yellow-900">
                          IDR {selectedCustomerDetail.summary?.total_money_credit || 0}
                        </p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-xs text-blue-600 mb-1">Time Credit</p>
                        <p className="text-xl font-semibold text-blue-900">
                          {selectedCustomerDetail.summary?.total_time_credit_minutes || 0} m
                        </p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <p className="text-xs text-purple-600 mb-1">Passes</p>
                        <p className="text-xl font-semibold text-purple-900">
                          {selectedCustomerDetail.summary?.total_passes || 0}
                        </p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <p className="text-xs text-green-600 mb-1">Printing</p>
                        <p className="text-xl font-semibold text-green-900">
                          {selectedCustomerDetail.summary?.total_printing_credit || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Benefits Details Section */}
                  {selectedCustomerDetail.benefit_transactions && selectedCustomerDetail.benefit_transactions.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" /> Transaction History
                      </h3>
                      
                      <div className="space-y-6">
                        {/* Money Credit List */}
                        {selectedCustomerDetail.benefit_transactions.filter(t => t.benefitType === 'money_credit').length > 0 && (
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                              <span className="text-xs font-bold text-gray-700 uppercase">Money Credit Consumed</span>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {selectedCustomerDetail.benefit_transactions
                                .filter(t => t.benefitType === 'money_credit')
                                .map((item, index) => (
                                <div key={index} className="px-4 py-3 flex justify-between items-center text-sm hover:bg-gray-50">
                                  <div>
                                    <span className="text-gray-900 font-medium block">Deducted: {item.amount}</span>
                                    <span className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleDateString()}</span>
                                  </div>
                                  <span className="text-gray-600 text-right">{item.bookingDetails || 'N/A'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Time Credit List */}
                        {selectedCustomerDetail.benefit_transactions.filter(t => t.benefitType === 'time_credit').length > 0 && (
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                              <span className="text-xs font-bold text-gray-700 uppercase">Time Credit Consumed</span>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {selectedCustomerDetail.benefit_transactions
                                .filter(t => t.benefitType === 'time_credit')
                                .map((item, index) => (
                                <div key={index} className="px-4 py-3 flex justify-between items-center text-sm hover:bg-gray-50">
                                  <div>
                                    <span className="text-gray-900 font-medium block">Deducted: {item.amount}</span>
                                    <span className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleDateString()}</span>
                                  </div>
                                  <span className="text-gray-600 text-right">{item.bookingDetails || 'N/A'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 7. INVOICES TAB */}
              {activeDetailTab === 'Invoices' && (
                <div className="space-y-8">
                  {/* Plan Invoices */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Plan Invoices
                    </h3>
                    {selectedCustomerDetail.plan_invoices && selectedCustomerDetail.plan_invoices.length > 0 ? (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                         <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                              <tr>
                                <th className="px-4 py-2">Invoice #</th>
                                <th className="px-4 py-2">Due Date</th>
                                <th className="px-4 py-2">Amount</th>
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {selectedCustomerDetail.plan_invoices.map((inv, idx) => (
                                <tr key={inv._id || idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium text-gray-900">{inv.sno || 'N/A'}</td>
                                  <td className="px-4 py-3 text-gray-500">{inv.dueDate ? formatDate(inv.dueDate) : 'N/A'}</td>
                                  <td className="px-4 py-3 text-gray-900">IDR {formatCurrency(inv.totalAmount || 0)}</td>
                                  <td className="px-4 py-3">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                      Issued
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    {inv.invoiceUrl && (
                                      <div className="flex items-center justify-end gap-2">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handlePreviewInvoice(inv.invoiceUrl, inv.sno); }}
                                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                          title="Preview PDF"
                                        >
                                          <Eye className="w-4 h-4" />
                                        </button>
                                        <a
                                          href={inv.invoiceUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                          title="Download PDF"
                                        >
                                          <Download className="w-4 h-4" />
                                        </a>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                      </div>
                    ) : (
                      <div className="p-6 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-center">
                        <p className="text-sm text-gray-500">No plan invoices available.</p>
                      </div>
                    )}
                  </div>

                  {/* Booking Invoices */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Booking Invoices
                    </h3>
                    {selectedCustomerDetail.booking_invoices && selectedCustomerDetail.booking_invoices.length > 0 ? (
                       <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm text-left">
                          <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-2">Invoice #</th>
                              <th className="px-4 py-2">Issue Date</th>
                              <th className="px-4 py-2">Amount</th>
                              <th className="px-4 py-2">Status</th>
                              <th className="px-4 py-2 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {selectedCustomerDetail.booking_invoices.map((inv, idx) => (
                              <tr key={inv._id || idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-900">{inv.invoice_number || 'N/A'}</td>
                                <td className="px-4 py-3 text-gray-500">{inv.issue_date ? formatDate(inv.issue_date) : 'N/A'}</td>
                                <td className="px-4 py-3 text-gray-900">IDR {formatCurrency(inv.total_amount || 0)}</td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                                    inv.status === 'paid' ? 'bg-green-100 text-green-800' :
                                    inv.status === 'overdue' ? 'bg-red-100 text-red-800' :
                                    inv.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {inv.status || 'draft'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {inv.pdf_path && (
                                    <div className="flex items-center justify-end gap-2">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handlePreviewInvoice(inv.pdf_path, inv.invoice_number); }}
                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Preview PDF"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                      <a
                                        href={inv.pdf_path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Download PDF"
                                      >
                                        <Download className="w-4 h-4" />
                                      </a>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-6 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-center">
                        <p className="text-sm text-gray-500">No booking invoices available.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NEW MODALS FOR DEPOSITS, CREDITS, AND INVOICING */}

      {/* 1. Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{isEditingDeposit ? 'Edit Deposit' : 'Add Deposit to Plan'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Select Product</label>
                <select 
                  value={depositFormData.productId} 
                  onChange={handleProductSelectForDeposit} 
                  disabled={isEditingDeposit} 
                  className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">-- Choose a Product --</option>
                  {allProducts.map(p => (
                    <option key={p._id || p.id} value={p._id || p.id}>
                      {p.name || p.product_name} (IDR {formatCurrency(p.pricing?.unit_price || p.price || 0)})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Override Purchase Price (IDR)</label>
                <input 
                  type="number" 
                  value={depositFormData.purchase_price} 
                  onChange={(e) => setDepositFormData({...depositFormData, purchase_price: e.target.value})} 
                  className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" 
                  placeholder="Enter custom price or leave default"
                />
              </div>
              <p className="text-xs text-gray-500 italic mt-2">
                * Note: The final deposit total on the invoice will be multiplied by the plan quantity (x{activePlanForDeposit?.quantity || 1}).
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowDepositModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSubmitDeposit} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save Deposit</button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Manual Credit Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Manual Credit</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Credit Type</label>
                <select value={creditFormData.type} onChange={(e) => setCreditFormData({...creditFormData, type: e.target.value})} className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="money_credit">Money Credit (IDR)</option>
                  <option value="time_credit">Time Credit</option>
                  <option value="passes">Passes (Quantity)</option>
                  <option value="printing_credit">Printing Credit</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Amount / Value</label>
                <input type="number" value={creditFormData.amount} onChange={(e) => setCreditFormData({...creditFormData, amount: e.target.value})} className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="e.g. 100" />
              </div>
              
              {/* Only show Unit dropdown if time_credit is selected */}
              {creditFormData.type === 'time_credit' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Time Unit</label>
                  <select value={creditFormData.unit} onChange={(e) => setCreditFormData({...creditFormData, unit: e.target.value})} className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Expires In (Days)</label>
                <input type="number" value={creditFormData.expireInDays} onChange={(e) => setCreditFormData({...creditFormData, expireInDays: e.target.value})} className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowCreditModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleAddManualCredit} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Add Credit</button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Generate Plan Invoice Modal */}
      {showPlanInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Plan Invoice</h3>
            <div className="grid grid-cols-2 gap-5">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={invoiceFormData.description} onChange={(e) => handleInvoiceChange('description', e.target.value)} className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Base Plan Price (IDR)</label>
                <input type="number" value={invoiceFormData.price} onChange={(e) => handleInvoiceChange('price', e.target.value)} className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tax (%)</label>
                <input type="number" value={invoiceFormData.tax} onChange={(e) => handleInvoiceChange('tax', e.target.value)} className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              
              {/* Dynamic Read-only Deposit Overview */}
              {invoiceFormData.deposits && invoiceFormData.deposits.length > 0 && (
                <div className="col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="text-xs font-semibold text-gray-800 mb-2">Included Deposits / Products (Uninvoiced)</h4>
                  <div className="space-y-1">
                    {invoiceFormData.deposits.map((d, i) => (
                      <div key={i} className="flex justify-between text-xs text-gray-600 border-b border-gray-100 pb-1">
                        <span>{d.productName} (Unit Price: IDR {formatCurrency(d.price)} x {d.quantity})</span>
                        <span className="font-medium text-gray-900">IDR {formatCurrency(d.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Calculated Total Amount (Inc. Tax on Plan)</label>
                <input type="number" value={invoiceFormData.totalAmount} readOnly className="w-full border border-gray-300 bg-gray-100 text-gray-700 font-semibold px-3 py-2 rounded-lg text-sm cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                <input type="date" value={invoiceFormData.dueDate} onChange={(e) => handleInvoiceChange('dueDate', e.target.value)} className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>

            {/* Added Payment / Bank Details Section */}
            <div className="col-span-2 pt-4 mt-2 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Payment Details (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Bank Name</label>
                  <input
                    type="text"
                    value={invoiceFormData.bank_name}
                    onChange={(e) => handleInvoiceChange('bank_name', e.target.value)}
                    placeholder="e.g. BCA"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Bank Code</label>
                  <input
                    type="text"
                    value={invoiceFormData.bank_code}
                    onChange={(e) => handleInvoiceChange('bank_code', e.target.value)}
                    placeholder="e.g. 23456"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Account Number</label>
                  <input
                    type="text"
                    value={invoiceFormData.account_number}
                    onChange={(e) => handleInvoiceChange('account_number', e.target.value)}
                    placeholder="e.g. 87654567876"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Sort Code</label>
                  <input
                    type="text"
                    value={invoiceFormData.sort_code}
                    onChange={(e) => handleInvoiceChange('sort_code', e.target.value)}
                    placeholder="e.g. 987654"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowPlanInvoiceModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreatePlanInvoice} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Generate & Save PDF</button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay Message */}
      {isOverlayOpen && (
        <div className="fixed top-4 right-4 z-[100] animate-slide-in">
          <div className="bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg">
            <p className="text-sm">{overlayMessage}</p>
          </div>
        </div>
      )}

      <PDFPreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewData({ url: '', number: '' });
        }}
        pdfUrl={previewData.url}
        invoiceNumber={previewData.number}
      />
    </div>
  );
}

function PDFPreviewModal({ isOpen, onClose, pdfUrl, invoiceNumber }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Invoice Preview</h2>
            <p className="text-sm text-gray-500 mt-1">{invoiceNumber}</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <iframe
            src={pdfUrl}
            className="w-full h-full"
            title={`Invoice ${invoiceNumber}`}
          />
        </div>
      </div>
    </div>
  );
}